import React from 'react';
import './ApplicationMilestones.css';

export default function ApplicationMilestones({ applicant }) {
  if (!applicant) return null;

  // Define all milestones with their target dates
  const milestones = [
    {
      key: 'first_contact',
      title: 'First Contact',
      description: 'Initial inquiry received',
      targetDate: applicant.created_at || applicant.first_contact_date,
      completedDate: applicant.first_contact_date,
      status: 'completed',
      icon: '✓'
    },
    {
      key: 'application_submitted',
      title: 'Application Submitted',
      description: 'Complete application with documents',
      targetDate: applicant.applied_at || (applicant.created_at ? new Date(new Date(applicant.created_at).getTime() + 2 * 24 * 60 * 60 * 1000).toISOString() : null),
      completedDate: applicant.applied_at,
      status: applicant.applied_at ? 'completed' : 'pending',
      icon: '✓'
    },
    {
      key: 'first_followup',
      title: 'First Follow-up',
      description: 'Check on missing documents',
      targetDate: applicant.first_followup_date || (applicant.applied_at ? new Date(new Date(applicant.applied_at).getTime() + 3 * 24 * 60 * 60 * 1000).toISOString() : null),
      completedDate: applicant.first_followup_date,
      status: applicant.first_followup_date ? 'completed' : 'overdue',
      icon: '✓'
    },
    {
      key: 'screening_complete',
      title: 'Screening Complete',
      description: 'Background & credit check done',
      targetDate: applicant.screening_date || (applicant.applied_at ? new Date(new Date(applicant.applied_at).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString() : null),
      completedDate: applicant.background_checked_at || applicant.screening_date,
      status: applicant.background_checked_at || applicant.screening_date ? 'completed' : 'overdue',
      icon: '📄'
    },
    {
      key: 'decision_deadline',
      title: 'Decision Deadline',
      description: 'Approve or reject application',
      targetDate: applicant.decision_date || (applicant.applied_at ? new Date(new Date(applicant.applied_at).getTime() + 10 * 24 * 60 * 60 * 1000).toISOString() : null),
      completedDate: applicant.approved_at || applicant.decision_date,
      status: applicant.approved_at || applicant.decision_date ? 'completed' : 'overdue',
      icon: '🕐'
    },
    {
      key: 'lease_submission',
      title: 'Lease Submission',
      description: 'Send lease for signature',
      targetDate: applicant.lease_submission_date || (applicant.approved_at ? new Date(new Date(applicant.approved_at).getTime() + 2 * 24 * 60 * 60 * 1000).toISOString() : null),
      completedDate: applicant.lease_submission_date,
      status: applicant.lease_submission_date ? 'completed' : 'overdue',
      icon: '✏️'
    },
    {
      key: 'lease_signed',
      title: 'Lease Signed',
      description: 'All parties sign the lease',
      targetDate: applicant.lease_signed_at || (applicant.lease_submission_date ? new Date(new Date(applicant.lease_submission_date).getTime() + 2 * 24 * 60 * 60 * 1000).toISOString() : null),
      completedDate: applicant.lease_signed_at,
      status: applicant.lease_signed_at ? 'completed' : 'overdue',
      icon: '📄'
    },
    {
      key: 'movein_ready',
      title: 'Move-in Ready',
      description: 'Keys handed over',
      targetDate: applicant.move_in_date || (applicant.lease_signed_at ? new Date(new Date(applicant.lease_signed_at).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString() : null),
      completedDate: applicant.move_in_date || applicant.moved_in_date,
      status: applicant.move_in_date || applicant.moved_in_date ? 'completed' : 'overdue',
      icon: '🔑'
    }
  ];

  // Calculate statistics
  const completedCount = milestones.filter(m => m.status === 'completed').length;
  const overdueCount = milestones.filter(m => m.status === 'overdue').length;
  const totalCount = milestones.length;

  // Calculate days overdue
  const getDaysOverdue = (targetDate) => {
    if (!targetDate) return 0;
    const target = new Date(targetDate);
    const now = new Date();
    const diffTime = now - target;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Get first contact date
  const firstContactDate = applicant.created_at || applicant.first_contact_date || applicant.applied_at;


  return (
    <div className="application-milestones">
      <div className="milestones-header">
        <div className="milestones-header-left">
          <h2 className="milestones-title">
            <span className="milestones-title-icon">📅</span>
            Application Milestones
          </h2>
          {firstContactDate && (
            <p className="milestones-first-contact">
              First contact: {formatDate(firstContactDate)}
            </p>
          )}
        </div>
        <div className="milestones-header-right">
          <span className="milestone-badge complete-badge">
            {completedCount}/{totalCount} Complete
          </span>
          {overdueCount > 0 && (
            <span className="milestone-badge overdue-badge">
              {overdueCount} Overdue
            </span>
          )}
        </div>
      </div>

      <div className="milestones-list">
        {milestones.map((milestone, index) => {
          const isCompleted = milestone.status === 'completed';
          const isOverdue = milestone.status === 'overdue';
          const daysOverdue = isOverdue ? getDaysOverdue(milestone.targetDate) : 0;
          const isLast = index === milestones.length - 1;

          return (
            <div key={milestone.key} className={`milestone-card ${milestone.status}`}>
              <div className="milestone-left">
                <div className={`milestone-icon ${isCompleted ? 'completed-icon' : isOverdue ? 'overdue-icon' : 'pending-icon'}`}>
                  <span>{milestone.icon}</span>
                </div>
                {!isLast && (
                  <div className={`milestone-connector ${isCompleted ? 'completed-connector' : isOverdue ? 'overdue-connector' : 'pending-connector'}`} />
                )}
              </div>
              <div className="milestone-content">
                <div className="milestone-header-content">
                  {isOverdue ? (
                    <div className="milestone-title-wrapper">
                      <h3 className="milestone-title">{milestone.title}</h3>
                      <span className="milestone-overdue-tag">Overdue</span>
                    </div>
                  ) : (
                    <h3 className="milestone-title">{milestone.title}</h3>
                  )}
                  <div className="milestone-date">
                    {formatDate(isCompleted ? (milestone.completedDate || milestone.targetDate) : milestone.targetDate)}
                  </div>
                </div>
                <p className="milestone-description">{milestone.description}</p>
                <span className={`milestone-status ${isCompleted ? 'completed-status' : isOverdue ? 'overdue-status' : 'pending-status'}`}>
                  {isCompleted 
                    ? 'Completed' 
                    : isOverdue 
                    ? `${daysOverdue} days ago`
                    : 'Pending'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
